<template>
  <div class="page">
    <TopNav
        activeLv1="lotto"
        activeLv2="lotto"
        @nav="onNav"
        @sub="onSub"
    />

    <div class="layout">
      <LeftPanel
          :dateText="dateText"
          :currency="currency"
          :balance="balance"
          :outstanding="outstanding"
          :last5="last5"
          :currentIssueShort="currentIssueShort"
          :currentBets="currentBets"
          :prevGroups="prevGroups"
          :loadingBalance="loadingBalance"
          :loadingLast="loadingLast"
          :loadingTicket="loadingTicket"
          :loadingPrev="loadingPrev"
          :kenoConfig="kenoConfig"
          @refresh-balance="() => handleLeftRefresh('balance')"
          @refresh-last="() => handleLeftRefresh('last5')"
          @refresh-ticket="() => handleLeftRefresh('tickets')"
          @refresh-prev="() => handleLeftRefresh('lastSettled')"
      />

      <div class="main">
        <CenterBoard
            v-if="_centerBoardReady"
            ref="centerBoard"
            :winCode="winCode"
            :config="kenoConfig"
            :bettingPhase="bettingPhase"
            :uiBettingAllowed="uiBettingAllowed"
            :betSecondsLeft="betSecondsLeft"
            :displaySeconds="displayCountdownSeconds"
            :timeText="timeText"
            :prevIssueShort="prevIssueShort"
            :prevTotal="prevTotal"
            :currentIssueShort="currentIssueShort"
            :ouGrid="ouGrid"
            :rangeGrid="rangeGrid"
            :hitNumbers="hitNumbers"
            :boardHitNumbers="boardHitNumbers"
            :drawRollingNumber="drawRollingNumber"
            :drawSum="drawSum"
            :drawSecondsLeftText="drawSecondsLeftText"
            :drawIndex="drawIndex"
            @pick="onPick"
        />
      </div>
    </div>

    <BetModal
        :visible="betModalVisible"
        :issueShort="currentIssueShort"
        :timeText="timeText"
        :playLabel="betModalPayload.playLabel"
        :rangeText="betModalPayload.rangeText"
        :oddsText="betModalPayload.oddsText"
        :minBet="kenoConfig.minBet"
        :maxBet="kenoConfig.maxBet"
        :maxPayoutText="'-'"
        @cancel="onBetModalCancel"
        @confirm="onBetModalConfirm"
    />
  </div>
</template>

<script>
import TopNav from "@/components/TopNav";
import LeftPanel from "@/components/LeftPanel";
import CenterBoard from "@/components/CenterBoard.vue";
import BetModal from "@/components/BetModal.vue";

import { getBalanceApi } from "@/api/wallet";
import { getCurrentIssueApi } from "@/api/issue";
import { getBetHistoryApi, placeBetApi } from "@/api/bet";
import {
  getKenoConfigApi,
  getLastSettledIssuesApi,
  revealStateApi
} from "@/api/keno";
import { buildRoadmap } from "@/utils/roadmap";

const REVEAL_TOTAL = 20;

export default {
  name: "Lotto",
  components: { TopNav, LeftPanel, CenterBoard, BetModal },

  data() {
    return {
      nowTs: Date.now(),

      // ===== 页面阶段 =====
      bettingPhase: "BETTING", // BETTING / DRAWING
      currentPhase: "betting", // betting / revealing / pause / nextIssue

      // ===== 当前期 =====
      currentIssueNo: "",
      lastIssueNo: "",

      // ===== 服务端时间模型 =====
      serverTimeMs: 0,
      serverOffsetMs: 0,
      betCutoffTimeMs: 0,
      closeTimeMs: 0,
      displaySecondsSeed: 0,
      displayBetSecondsSeed: 0,
      _seedServerTimeMs: 0,
      _seedCloseTimeMs: 0,
      _seedBetCutoffTimeMs: 0,

      // ===== 轮询 / 本地时钟 =====
      issueClockTimer: null,
      revealPollTimer: null,
      _isRevealPolling: false,
      _isSwitchingToReveal: false,
      _hasTriggeredReveal: false,

      // ===== 页面恢复 =====
      isPageHidden: false,
      _lastResumeAt: 0,
      _resuming: false,

      // ===== 初始化 =====
      _centerBoardReady: false,

      // ===== LeftPanel =====
      balance: 0,
      currency: "KHR",
      outstanding: 0,
      last5: [],
      currentBets: [],
      prevGroups: [],
      loadingBalance: false,
      loadingLast: false,
      loadingTicket: false,
      loadingPrev: false,

      // ===== config =====
      kenoConfig: {
        currency: "KHR",
        cycleSeconds: 200,
        minBet: 0,
        maxBet: 0,
        under: { min: "-", max: "-", odds: "-" },
        over: { min: "-", max: "-", odds: "-" },
        ranges: [],
        revealIntervalMs: 2500,
        resultPauseSecs: 5
      },

      // ===== countdown =====
      countdownSecondsLeft: 0,
      displaySecondsLeft: 0,
      betSecondsLeft: 0,
      uiBettingAllowed: false,

      // ===== prev issue =====
      prevIssueNo: "",
      prevTotal: "-",

      // ===== roadmaps =====
      ouGrid: [],
      rangeGrid: [],

      // ===== reveal state =====
      revealState: {
        issueNo: "",
        revealedCount: 0,
        revealedNumbers: [],
        total: REVEAL_TOTAL,
        done: true,
        intervalMs: 1500
      },

      // ===== draw animation state =====
      revealIdx: 0,
      revealDone: true,
      revealIssueNo: "",

      hitNumbers: [],
      lastSettledHitNumbers: [],
      drawRollingNumber: 0,
      drawSum: 0,
      drawIndex: 0,
      winCode: "",

      _pendingFinalState: null,
      _flashedIssueNo: "",
      _seenDrawingIssueNo: "",
      _currentRevealIssueNo: "",
      _lastProcessedCount: 0,
      _isAnimating: false,
      _uiLocked: false,

      // ===== rolling animation =====
      _raf: null,

      // ===== bet modal =====
      betModalVisible: false,
      betModalPayload: {
        code: "",
        playLabel: "",
        rangeText: "",
        oddsText: ""
      }
    };
  },

  computed: {
    dateText() {
      const d = new Date(this.nowTs);
      const utcMs = d.getTime() + d.getTimezoneOffset() * 60 * 1000;
      const gmt7 = new Date(utcMs + 7 * 60 * 60 * 1000);
      const y = gmt7.getFullYear();
      const m = String(gmt7.getMonth() + 1).padStart(2, "0");
      const day = String(gmt7.getDate()).padStart(2, "0");
      return `${y}-${m}-${day}`;
    },

    realSecondsLeft() {
      return Number(this.countdownSecondsLeft || 0);
    },

    displaySeconds() {
      return Number(this.displaySecondsLeft || 0);
    },

    displayCountdownSeconds() {
      return Number(this.displaySecondsLeft || 0);
    },

    isSealedPhase() {
      return this.displaySeconds <= 6 && this.displaySeconds > 0;
    },

    canBet() {
      return !!this.uiBettingAllowed;
    },

    drawSecondsLeftText() {
      if (this.revealState?.done) return "";
      if (!this.revealState?.intervalMs) return "";

      const totalTime = REVEAL_TOTAL * Number(this.revealState.intervalMs || 1500);
      const elapsed = Number(this.revealState.revealedCount || 0) * Number(this.revealState.intervalMs || 1500);
      const left = Math.max(0, totalTime - elapsed);
      return Math.ceil(left / 1000);
    },

    timeText() {
      const d = new Date(this.nowTs);
      const utcMs = d.getTime() + d.getTimezoneOffset() * 60 * 1000;
      const gmt7 = new Date(utcMs + 7 * 60 * 60 * 1000);
      const hh = String(gmt7.getHours()).padStart(2, "0");
      const mm = String(gmt7.getMinutes()).padStart(2, "0");
      const ss = String(gmt7.getSeconds()).padStart(2, "0");
      return `GMT+7 ${hh}:${mm}:${ss}`;
    },

    currentIssueShort() {
      const issueNo = this.currentIssueNo || "";
      const revealIssueNo = this.revealState?.issueNo || "";

      const frontDrawing =
          !!revealIssueNo &&
          revealIssueNo === this._currentRevealIssueNo &&
          (
              (this.hitNumbers && this.hitNumbers.length < REVEAL_TOTAL) ||
              (this._lastProcessedCount < REVEAL_TOTAL)
          );

      if (issueNo) {
        const arr = String(issueNo).split("#");
        return arr.length > 1 ? arr[1] : issueNo;
      }

      if (revealIssueNo && (!this.revealState?.done || frontDrawing)) {
        const arr = String(revealIssueNo).split("#");
        return arr.length > 1 ? arr[1] : revealIssueNo;
      }

      return "-";
    },

    prevIssueShort() {
      const no = this.prevIssueNo || "";
      if (!no) return "-";
      const arr = String(no).split("#");
      return arr.length > 1 ? arr[1] : no;
    },

    boardHitNumbers() {
      if (this.currentPhase === "revealing") {
        return Array.isArray(this.hitNumbers) ? this.hitNumbers : [];
      }
      if (Array.isArray(this.lastSettledHitNumbers) && this.lastSettledHitNumbers.length) {
        return this.lastSettledHitNumbers;
      }
      return Array.isArray(this.hitNumbers) ? this.hitNumbers : [];
    }
  },

  async mounted() {
    this.$updateViewport(1730);

    document.addEventListener("visibilitychange", this.handleVisibilityChange);
    window.addEventListener("focus", this.handleWindowFocus);

    await this.refreshAll();
  },

  activated() {
    this.startIssueClock();
    if (this.currentPhase === "revealing") {
      this.startRevealPolling();
    }
  },

  deactivated() {
    this.stopIssueClock();
    this.stopRevealPolling();
    this.stopRolling();
  },

  beforeDestroy() {
    document.removeEventListener("visibilitychange", this.handleVisibilityChange);
    window.removeEventListener("focus", this.handleWindowFocus);

    this.stopIssueClock();
    this.stopRevealPolling();
    this.stopRolling();
  },

  methods: {
    // ================== NAV ==================
    onNav(key) {
      if (key === "news") {
        window.location.reload();
        return;
      }
      if (key === "lotto") {
        this.$router.push("/lotto");
        return;
      }
      if (key === "lottery") {
        this.$router.push("/lottery");
        return;
      }
      if (key === "report") {
        this.$router.push("/report");
        return;
      }
      if (key === "balance") {
        this.$router.push("/balance");
        return;
      }
      if (key === "changePassword") {
        this.$router.push("/change-password");
        return;
      }
      alert("Coming soon");
    },

    onSub(key) {
      if (key === "lotto") return;
      if (key === "winResult" || key === "winResultV1") {
        this.$router.push("/report");
        return;
      }
      if (key === "result") {
        this.$router.push("/result");
        return;
      }
      alert("Coming soon");
    },

    // ================== 页面可见性恢复 ==================
    handleVisibilityChange() {
      if (document.hidden) {
        this.isPageHidden = true;
        return;
      }
      this.isPageHidden = false;
      this.resumePageState();
    },

    handleWindowFocus() {
      if (!document.hidden) {
        this.resumePageState();
      }
    },

    async resumePageState() {
      if (this._resuming) return;

      const now = Date.now();
      if (now - this._lastResumeAt < 1000) return;

      this._resuming = true;
      this._lastResumeAt = now;

      try {
        this.stopIssueClock();
        this.stopRevealPolling();

        await this.loadCurrentIssue();

        if (this.currentPhase === "revealing" || this.bettingPhase === "DRAWING") {
          await this.loadRevealState();
          await this.processRevealAnimation();

          if (this.revealState?.done === false) {
            this.currentPhase = "revealing";
            this.startRevealPolling();
          } else {
            await Promise.allSettled([
              this.loadBalance(),
              this.loadTicketsFromHistory(),
              this.refreshLastSettledBundle()
            ]);
            this.currentPhase = "betting";
            this.startIssueClock();
          }
        } else {
          this.startIssueClock();
        }
      } catch (e) {
        console.error("[resumePageState] 页面恢复失败:", e);
      } finally {
        this._resuming = false;
      }
    },

    // ================== 页面初始化 ==================
    async refreshAll() {
      try {
        await Promise.all([
          this.loadKenoConfig(),
          this.loadBalance(),
          this.loadRevealState()
        ]);

        await this.loadCurrentIssue();
        await this.refreshLastSettledBundle();
        await this.loadTicketsFromHistory();

        this._centerBoardReady = true;

        if (this.revealState?.issueNo && this.revealState?.done === false) {
          this.currentPhase = "revealing";
          this.bettingPhase = "DRAWING";
          this.startRevealPolling();
        } else {
          this.currentPhase = "betting";
          this.startIssueClock();
        }
      } catch (e) {
        console.error("[refreshAll] 初始化失败:", e);
      }
    },

    // ================== 本地时钟（不请求 current） ==================
    startIssueClock() {
      this.stopIssueClock();
      this.nowTs = Date.now();

      this.issueClockTimer = setInterval(() => {
        this.nowTs = Date.now();
        this.updateIssueClock();
      }, 200);
    },

    stopIssueClock() {
      if (this.issueClockTimer) {
        clearInterval(this.issueClockTimer);
        this.issueClockTimer = null;
      }
    },

    updateIssueClock() {
      if (!this.closeTimeMs || !this.betCutoffTimeMs) return;

      const nowMs = Date.now() + Number(this.serverOffsetMs || 0);

      const realSecondsLeft = Math.max(
          0,
          Math.floor((this.closeTimeMs - nowMs) / 1000)
      );

      const realBetSecondsLeft = Math.max(
          0,
          Math.floor((this.betCutoffTimeMs - nowMs) / 1000)
      );

      this.countdownSecondsLeft = realSecondsLeft;

      const totalRealMs = Math.max(
          1,
          Number(this._seedCloseTimeMs || 0) - Number(this._seedServerTimeMs || 0)
      );
      const remainRealMs = Math.max(0, Number(this.closeTimeMs || 0) - nowMs);

      if (this.displaySecondsSeed > 0) {
        this.displaySecondsLeft = Math.max(
            0,
            Math.ceil((remainRealMs / totalRealMs) * this.displaySecondsSeed)
        );
      } else {
        this.displaySecondsLeft = realSecondsLeft;
      }

      const totalBetRealMs = Math.max(
          1,
          Number(this._seedBetCutoffTimeMs || 0) - Number(this._seedServerTimeMs || 0)
      );
      const remainBetRealMs = Math.max(0, Number(this.betCutoffTimeMs || 0) - nowMs);

      if (this.displayBetSecondsSeed > 0) {
        this.betSecondsLeft = Math.max(
            0,
            Math.ceil((remainBetRealMs / totalBetRealMs) * this.displayBetSecondsSeed)
        );
      } else {
        this.betSecondsLeft = realBetSecondsLeft;
      }

      this.uiBettingAllowed = nowMs < this.betCutoffTimeMs;

      if (this.betModalVisible && !this.uiBettingAllowed) {
        this.betModalVisible = false;
      }

      if (nowMs < this.closeTimeMs) {
        this.bettingPhase = "BETTING";
        this.currentPhase = "betting";
      } else {
        this.bettingPhase = "DRAWING";
        if (!this._hasTriggeredReveal && !this._isSwitchingToReveal) {
          this._hasTriggeredReveal = true;
          this.enterRevealPhase();
        }
      }
    },

    async enterRevealPhase() {
      if (this._isSwitchingToReveal) return;
      this._isSwitchingToReveal = true;

      try {
        this.stopIssueClock();
        this.currentPhase = "revealing";
        this.bettingPhase = "DRAWING";

        await this.loadRevealState();
        await this.processRevealAnimation();
        this.startRevealPolling();
      } catch (e) {
        console.error("[enterRevealPhase] 进入开奖阶段失败:", e);
      } finally {
        this._isSwitchingToReveal = false;
      }
    },

    // ================== 开奖阶段轮询（只轮询 reveal/state） ==================
    startRevealPolling() {
      if (this._isRevealPolling) return;

      this.stopRevealPolling();
      this._isRevealPolling = true;

      this.revealPollTimer = setInterval(async () => {
        if (this._isAnimating) return;

        try {
          this.nowTs = Date.now();
          await this.loadRevealState();
          await this.processRevealAnimation();
        } catch (e) {
          console.error("[RevealPolling] error:", e);
        }
      }, 1000);
    },

    stopRevealPolling() {
      this._isRevealPolling = false;
      if (this.revealPollTimer) {
        clearInterval(this.revealPollTimer);
        this.revealPollTimer = null;
      }
    },

    // ================== 左侧手动刷新 ==================
    async handleLeftRefresh(type) {
      try {
        switch (type) {
          case "balance":
            await this.loadBalance();
            break;
          case "last5":
            await this.loadLast();
            break;
          case "tickets":
            await this.loadTicketsFromHistory();
            break;
          case "lastSettled":
            this.loadingPrev = true;
            try {
              await this.refreshLastSettledBundle();
              await this.loadTicketsFromHistory();
            } finally {
              this.loadingPrev = false;
            }
            break;
          default:
            console.warn(`未知的刷新类型: ${type}`);
        }
      } catch (error) {
        console.error(`刷新 ${type} 失败:`, error);
        if (type === "lastSettled") this.loadingPrev = false;
      }
    },

    // ================== 当前期接口：只在首次进入 / 开奖后 / 页面恢复等必要时调用 ==================
    async loadCurrentIssue() {
      try {
        const res = await getCurrentIssueApi();
        const d = res?.data || {};

        const secondsLeft = Number(d.secondsLeft || 0);
        const displaySecondsLeft = Number(d.displaySecondsLeft || 0);
        const displayBetSecondsLeft = Number(d.displayBetSecondsLeft || 0);
        const betSecondsLeft = Number(d.betSecondsLeft || 0);
        const bettingAllowed = !!d.bettingAllowed;
        const newIssueNo = d.issueNo ? String(d.issueNo) : "";

        this.serverTimeMs = Number(d.serverTimeMs || Date.now());
        this.serverOffsetMs = this.serverTimeMs - Date.now();
        this.betCutoffTimeMs = Number(d.betCutoffTimeMs || 0);
        this.closeTimeMs = Number(d.closeTimeMs || 0);

        this.displaySecondsSeed = displaySecondsLeft > 0
            ? displaySecondsLeft
            : Math.ceil(secondsLeft * 150 / 200);
        this.displayBetSecondsSeed = displayBetSecondsLeft > 0
            ? displayBetSecondsLeft
            : betSecondsLeft;

        this._seedServerTimeMs = this.serverTimeMs;
        this._seedCloseTimeMs = this.closeTimeMs;
        this._seedBetCutoffTimeMs = this.betCutoffTimeMs;

        this.countdownSecondsLeft = secondsLeft;
        this.displaySecondsLeft = this.displaySecondsSeed;
        this.betSecondsLeft = this.displayBetSecondsSeed;
        this.uiBettingAllowed = bettingAllowed && (Date.now() + this.serverOffsetMs < this.betCutoffTimeMs);

        if (newIssueNo && newIssueNo !== this.currentIssueNo) {
          if (this.currentIssueNo) {
            this.lastIssueNo = this.currentIssueNo;
          }
          this.currentIssueNo = newIssueNo;
          this.resetForNewBettingIssue(newIssueNo);
          this._hasTriggeredReveal = false;
        }

        this.updateIssueClock();
      } catch (e) {
        console.error("获取当前期次错误:", e);
      }
    },

    // ================== 历史开奖 bundle：一次请求，同时更新 last5 + roadmap + prev issue + 上一期号码 ==================
    async refreshLastSettledBundle() {
      try {
        this.loadingLast = true;
        const res = await getLastSettledIssuesApi(20);
        const raw = res?.data;
        const arr = Array.isArray(raw)
            ? raw
            : (raw?.content && Array.isArray(raw.content)
                ? raw.content
                : (raw?.data && Array.isArray(raw.data) ? raw.data : []));

        await this.loadIssueRelationsFromArr(arr);

        const ouSeq = arr.slice().reverse().map(x => x.resultOverUnder).filter(Boolean);
        const rangeSeq = arr.slice().reverse().map(x => x.resultRange).filter(Boolean);

        this.ouGrid = buildRoadmap(ouSeq, 6, 20, (key) => ({ key, value: key }));
        this.rangeGrid = buildRoadmap(rangeSeq, 6, 20, (key) => ({
          key,
          value: String(key).replace("R", "")
        }));

        this.last5 = arr.slice(0, 5).map((x) => {
          const t = String(x.closeAt || x.openAt || "").slice(11, 16);
          return {
            issueNo: x.issueNo,
            timeText: t || "",
            sumText: x.drawSum != null ? String(x.drawSum) : "-"
          };
        });

        // ✅ 保存上一期开奖号码给左边80号码区
        if (arr.length > 0) {
          const latestSettled = arr[0];
          const nums = this.parseDrawNumbers(latestSettled.drawNumbers);
          if (nums.length) {
            this.lastSettledHitNumbers = nums;
          }
        }
      } catch (e) {
        console.error("获取历史开奖失败:", e);
        this.ouGrid = [];
        this.rangeGrid = [];
        this.last5 = [];
      } finally {
        this.loadingLast = false;
      }
    },

    async loadLastSettledAndRoadmaps() {
      await this.refreshLastSettledBundle();
    },

    async loadLast() {
      await this.refreshLastSettledBundle();
    },

    parseDrawNumbers(v) {
      if (!v) return [];
      if (Array.isArray(v)) {
        return v
            .map(n => Number(n))
            .filter(n => Number.isInteger(n) && n >= 1 && n <= 80)
            .slice(0, REVEAL_TOTAL);
      }

      const s = String(v).trim();
      if (!s) return [];

      return s
          .split(/[,\s|]+/)
          .map(x => Number(x))
          .filter(n => Number.isInteger(n) && n >= 1 && n <= 80)
          .slice(0, REVEAL_TOTAL);
    },

    // ================== 注单 ==================
    async loadTicketsFromHistory() {
      if (this.loadingTicket) return;

      this.loadingTicket = true;
      try {
        const PREV_ISSUE_COUNT = 3;
        const res = await getBetHistoryApi(0, 200);

        let list = [];
        if (res && res.data && Array.isArray(res.data.list)) {
          list = res.data.list;
        } else if (res && Array.isArray(res.data)) {
          list = res.data;
        } else if (res && res.data && Array.isArray(res.data.content)) {
          list = res.data.content;
        } else if (res && res.data && Array.isArray(res.data.data)) {
          list = res.data.data;
        }

        list.sort((a, b) => {
          const ta = String(a.createdAt || "");
          const tb = String(b.createdAt || "");
          if (ta && tb && ta !== tb) return tb.localeCompare(ta);
          return Number(b.id || 0) - Number(a.id || 0);
        });

        const rows = list.map(item => ({
          id: Number(item.id || 0),
          issueNo: String(item.issueNo || "").trim(),
          optionCode: String(item.optionCode || "-").trim(),
          amount: Number(item.amount || 0),
          odds: item.odds !== undefined && item.odds !== null ? Number(item.odds) : null,
          status: Number(item.status || 0),
          winAmount: item.winAmount !== undefined && item.winAmount !== null ? Number(item.winAmount) : null,
          createdAt: item.createdAt || "",
          playType: item.playType || "",
          settledAt: item.settledAt || ""
        }));

        const currentBets = [];
        let currentIssueNo = "";

        const unsettledBets = rows.filter(x => x.status === 0);
        if (unsettledBets.length > 0) {
          currentIssueNo = unsettledBets[0].issueNo;
          currentBets.push(...rows.filter(x => x.status === 0 && x.issueNo === currentIssueNo));
        }

        if (!currentIssueNo && this.currentIssueNo) {
          currentIssueNo = this.currentIssueNo;
        }

        const prevGroups = [];
        const settledRows = rows.filter(x => x.status === 1);

        const issueMap = new Map();
        settledRows.forEach(row => {
          if (!row.issueNo) return;
          if (!issueMap.has(row.issueNo)) issueMap.set(row.issueNo, []);
          issueMap.get(row.issueNo).push(row);
        });

        const sortedIssues = Array.from(issueMap.keys()).sort((a, b) => {
          const timeA = issueMap.get(a)[0]?.createdAt || "";
          const timeB = issueMap.get(b)[0]?.createdAt || "";
          return timeB.localeCompare(timeA);
        });

        const recentIssues = sortedIssues.slice(0, PREV_ISSUE_COUNT);
        for (const issueNo of recentIssues) {
          const bets = issueMap.get(issueNo);
          const totalWin = bets.reduce((sum, bet) => sum + (bet.winAmount || 0), 0);
          prevGroups.push({
            issueNo,
            issueShort: this.issueShort(issueNo),
            bets,
            totalWin
          });
        }

        this.currentIssueNo = currentIssueNo || this.currentIssueNo;
        this.currentBets = currentBets;
        this.prevGroups = prevGroups;
      } catch (error) {
        console.error("获取注单历史失败:", error);
        this.currentBets = [];
        this.prevGroups = [];
      } finally {
        this.loadingTicket = false;
      }
    },

    // ================== 余额 ==================
    async loadBalance() {
      if (this.loadingBalance) return;
      this.loadingBalance = true;

      try {
        const res = await getBalanceApi();
        const d = res?.data || {};
        this.balance = Number(d.balance || 0);
        this.currency = d.currency || "KHR";
        this.outstanding = Number(d.outstanding || 0);
      } catch (e) {
        console.error("获取余额失败:", e);
      } finally {
        this.loadingBalance = false;
      }
    },

    // ================== 配置 ==================
    async loadKenoConfig() {
      try {
        const res = await getKenoConfigApi();
        const c = res?.data || {};
        this.kenoConfig = {
          currency: c.currency || "KHR",
          cycleSeconds: Number(c.cycleSeconds || 200),
          minBet: c.minBet != null ? Number(c.minBet) : 0,
          maxBet: c.maxBet != null ? Number(c.maxBet) : 0,
          under: {
            min: c.under && c.under.min != null ? c.under.min : "-",
            max: c.under && c.under.max != null ? c.under.max : "-",
            odds: c.under && c.under.odds != null ? c.under.odds : "-"
          },
          over: {
            min: c.over && c.over.min != null ? c.over.min : "-",
            max: c.over && c.over.max != null ? c.over.max : "-",
            odds: c.over && c.over.odds != null ? c.over.odds : "-"
          },
          ranges: Array.isArray(c.ranges) ? c.ranges : [],
          revealIntervalMs: Number(c.revealIntervalMs || c.reveal_interval_ms || 2500),
          resultPauseSecs: Number(c.resultPauseSecs || c.result_pause_secs || 5)
        };
      } catch (e) {
        console.error("获取配置失败:", e);
      }
    },

    // ================== 上一期 / 当前期辅助 ==================
    async loadIssueRelationsFromArr(allIssues) {
      try {
        const arr = Array.isArray(allIssues) ? allIssues : [];
        if (arr.length === 0) return;

        const trueCurrentIssue = this.getTrueCurrentIssue();

        for (let i = 0; i < arr.length; i++) {
          const issueNo = String(arr[i].issueNo || "");
          if (!issueNo) continue;

          if (issueNo !== trueCurrentIssue) {
            const total = arr[i].drawSum != null ? String(arr[i].drawSum) : "-";
            this.prevIssueNo = issueNo;
            this.prevTotal = total;
            break;
          }
        }
      } catch (e) {
        console.error("加载期号关系失败:", e);
      }
    },

    getTrueCurrentIssue() {
      const currentOpenIssue = this.currentIssueNo || "";
      const revealIssueNo = this.revealState?.issueNo || "";
      const revealDone = this.revealState?.done || true;

      if (currentOpenIssue && this.countdownSecondsLeft > 0) return currentOpenIssue;
      if (revealIssueNo && !revealDone) return revealIssueNo;
      return "";
    },

    issueShort(issueNo) {
      if (!issueNo) return "-";
      const arr = String(issueNo).split("#");
      return arr.length > 1 ? arr[1] : issueNo;
    },

    mapBetRow(x) {
      return {
        id: x.id,
        issueNo: String(x.issueNo || ""),
        optionCode: x.optionCode || "-",
        amount: Number(x.amount || 0),
        odds: x.odds !== undefined && x.odds !== null ? Number(x.odds) : null,
        status: Number(x.status),
        winAmount: x.winAmount !== undefined && x.winAmount !== null ? Number(x.winAmount) : null,
        createdAt: x.createdAt
      };
    },

    // ================== reveal/state ==================
    async loadRevealState() {
      try {
        const res = await revealStateApi();
        const state = res?.data || {};

        const nums = Array.isArray(state.revealedNumbers)
            ? state.revealedNumbers
                .map(n => Number(n) || 0)
                .filter(n => n >= 1 && n <= 80)
            : [];

        const numsClamped = nums.slice(0, REVEAL_TOTAL);
        const cntRaw = Number(state.revealedCount || 0);
        const cntFix = Math.max(cntRaw, numsClamped.length);
        const cnt = Math.min(REVEAL_TOTAL, cntFix);

        const safeState = {
          issueNo: String(state.issueNo || ""),
          revealedCount: cnt,
          revealedNumbers: numsClamped,
          total: REVEAL_TOTAL,
          done: !!state.done,
          intervalMs: Number(state.intervalMs || 1500)
        };

        const prev = this.revealState || { issueNo: "", done: true };

        if (
            safeState.issueNo &&
            safeState.done === false &&
            (
                safeState.issueNo !== this._currentRevealIssueNo ||
                prev.done === true ||
                !this._currentRevealIssueNo
            )
        ) {
          this.resetForNewIssue(safeState);
        }

        if (
            safeState.issueNo &&
            safeState.done === false &&
            safeState.revealedCount > 0 &&
            this.hitNumbers.length === 0
        ) {
          const numbersToShow = safeState.revealedNumbers.slice(0, safeState.revealedCount);
          this.hitNumbers = [...numbersToShow];
          this.drawSum = this.hitNumbers.reduce((sum, num) => sum + (num || 0), 0);
          this.drawIndex = this.hitNumbers.length;
          this._lastProcessedCount = safeState.revealedCount;
          this._currentRevealIssueNo = safeState.issueNo;
        }

        this.revealState = safeState;

        if (safeState.issueNo && safeState.done === false) {
          this._seenDrawingIssueNo = safeState.issueNo;
        }
      } catch (e) {
        console.error("获取reveal状态错误:", e);
        this.revealState = {
          issueNo: "",
          revealedCount: 0,
          revealedNumbers: [],
          total: REVEAL_TOTAL,
          done: true,
          intervalMs: 1500
        };
      }
    },

    resetForNewIssue(state) {
      this._currentRevealIssueNo = state.issueNo;
      this._lastProcessedCount = 0;
      this._uiLocked = true;

      this.hitNumbers = [];
      this.drawSum = 0;
      this.drawRollingNumber = 0;
      this.drawIndex = 0;
      this.stopRolling();

      if (state.revealedCount > 0 && state.revealedNumbers.length > 0) {
        const numbersToShow = state.revealedNumbers.slice(0, state.revealedCount);
        this.hitNumbers = [...numbersToShow];
        this.drawSum = this.hitNumbers.reduce((sum, num) => sum + (num || 0), 0);
        this.drawIndex = this.hitNumbers.length;
        this._lastProcessedCount = state.revealedCount;
      }
    },

    resetForNewBettingIssue(issueNo) {
      console.log("[resetForNewBettingIssue] 新一期投注开始:", issueNo);

      this.revealIdx = 0;
      this.revealDone = true;
      this.revealIssueNo = "";
      this._currentRevealIssueNo = "";
      this._lastProcessedCount = 0;

      // ✅ 不清 lastSettledHitNumbers，只清当前开奖态
      this.hitNumbers = [];
      this.drawSum = 0;
      this.drawRollingNumber = 0;
      this.drawIndex = 0;
      this.winCode = "";
      this.stopRolling();

      this._uiLocked = false;
      this._isAnimating = false;

      this._pendingFinalState = null;
      this._flashedIssueNo = "";
      this._seenDrawingIssueNo = "";

      this.currentPhase = "betting";
      this.bettingPhase = "BETTING";
    },

    async processRevealAnimation() {
      const state = this.revealState;
      if (!state || !state.issueNo) return;

      const backendCount = Math.min(
          REVEAL_TOTAL,
          Number(state.revealedCount || 0),
          Array.isArray(state.revealedNumbers) ? state.revealedNumbers.length : 0
      );

      if (this._lastProcessedCount > REVEAL_TOTAL) {
        this._lastProcessedCount = REVEAL_TOTAL;
      }

      if (state.done && state.revealedNumbers.length >= REVEAL_TOTAL) {
        if (this._seenDrawingIssueNo !== state.issueNo) {
          this._pendingFinalState = null;
        } else if (!this._pendingFinalState || this._pendingFinalState.issueNo !== state.issueNo) {
          this._pendingFinalState = {
            issueNo: state.issueNo,
            nums: [...state.revealedNumbers.slice(0, REVEAL_TOTAL)]
          };
        }
      }

      if (
          state.issueNo === this._currentRevealIssueNo &&
          backendCount > this._lastProcessedCount
      ) {
        this._uiLocked = true;

        const newNumbers = state.revealedNumbers.slice(
            this._lastProcessedCount,
            backendCount
        );

        for (let i = 0; i < newNumbers.length; i++) {
          if (state.issueNo !== this._currentRevealIssueNo) break;
          while (this._isAnimating) {
            await this.sleep(60);
          }
          await this.showOneBall(newNumbers[i]);
        }

        this._lastProcessedCount = backendCount;
        this.revealIdx = backendCount;
      }

      const pf = this._pendingFinalState;
      const frontDone =
          this.hitNumbers.length >= REVEAL_TOTAL &&
          this._lastProcessedCount >= REVEAL_TOTAL;

      if (pf && pf.issueNo === state.issueNo && frontDone && !this._isAnimating) {
        if (this._flashedIssueNo !== pf.issueNo) {
          this._flashedIssueNo = pf.issueNo;

          this.hitNumbers = [...pf.nums];
          this.drawSum = this.hitNumbers.reduce((s, n) => s + (Number(n) || 0), 0);
          this.drawIndex = REVEAL_TOTAL;
          this.stopRolling();

          const { ouCode, rangeCode } = this.calcWinCodesBySum(this.drawSum);
          this.winCode = "";

          setTimeout(() => {
            this.winCode = `${ouCode}|${rangeCode}`;
          }, 30);

          const FLASH_TOTAL_MS = 5000;
          const PAUSE_MS = (this.kenoConfig?.resultPauseSecs || 5) * 1000;

          setTimeout(() => {
            this._uiLocked = false;
            this._pendingFinalState = null;
            this.completeReveal().catch(err => console.error("completeReveal error:", err));
          }, Math.max(FLASH_TOTAL_MS, PAUSE_MS));
        }
        return;
      }

      const backendDone = state.done && backendCount >= REVEAL_TOTAL;
      const thisIssueIsOurs = state.issueNo === this._currentRevealIssueNo;

      if (backendDone && thisIssueIsOurs && !this._isAnimating) {
        if (this._flashedIssueNo !== state.issueNo) {
          this._flashedIssueNo = state.issueNo;

          this.hitNumbers = [...state.revealedNumbers.slice(0, REVEAL_TOTAL)];
          this.drawSum = this.hitNumbers.reduce((s, n) => s + (Number(n) || 0), 0);
          this.drawIndex = REVEAL_TOTAL;
          this._lastProcessedCount = REVEAL_TOTAL;
          this.stopRolling();

          const { ouCode, rangeCode } = this.calcWinCodesBySum(this.drawSum);
          this.winCode = "";

          setTimeout(() => {
            this.winCode = `${ouCode}|${rangeCode}`;
          }, 30);

          const FLASH_TOTAL_MS = 5000;
          const PAUSE_MS = (this.kenoConfig?.resultPauseSecs || 5) * 1000;

          setTimeout(() => {
            this._pendingFinalState = null;
            this._uiLocked = false;
            this.completeReveal().catch(err => console.error("completeReveal error:", err));
          }, Math.max(FLASH_TOTAL_MS, PAUSE_MS));
        }
        return;
      }

      if (
          state.revealedCount < REVEAL_TOTAL &&
          !state.done &&
          !this._isAnimating &&
          this.hitNumbers.length < REVEAL_TOTAL
      ) {
        if (state.issueNo === this._currentRevealIssueNo) {
          this.startRolling();
        }
      }
    },

    async completeReveal() {
      this.stopRevealPolling();

      // ✅ 保存上一期号码给下注阶段 80 个号码区继续显示
      if (Array.isArray(this.hitNumbers) && this.hitNumbers.length) {
        this.lastSettledHitNumbers = [...this.hitNumbers];
      }

      this._pendingFinalState = null;
      this._uiLocked = false;
      this._isAnimating = false;

      this.hitNumbers = [];
      this.drawSum = 0;
      this.drawIndex = 0;
      this.drawRollingNumber = 0;
      this.winCode = "";

      await this.$nextTick();
      this.stopRolling();

      this._currentRevealIssueNo = "";
      this._lastProcessedCount = 0;

      await this.sleep(50);

      try {
        await Promise.allSettled([
          this.loadCurrentIssue(),
          this.loadBalance(),
          this.loadTicketsFromHistory(),
          this.refreshLastSettledBundle()
        ]);

        this.currentPhase = "betting";
        this.bettingPhase = "BETTING";
        this._hasTriggeredReveal = false;

        this.startIssueClock();
      } catch (e) {
        console.error("开奖后刷新数据失败:", e);
      }
    },

    // ================== 动画 ==================
    startRolling() {
      if (this._raf) cancelAnimationFrame(this._raf);

      const TICK_MS = 90;
      let lastTs = 0;

      const roll = () => {
        if (!this._raf) return;

        const now = performance.now();
        if (!lastTs || now - lastTs >= TICK_MS) {
          this.drawRollingNumber = 1 + Math.floor(Math.random() * 80);
          lastTs = now;
        }

        this._raf = requestAnimationFrame(roll);
      };

      this._raf = requestAnimationFrame(roll);
    },

    stopRolling() {
      if (this._raf) {
        cancelAnimationFrame(this._raf);
        this._raf = null;
      }
      this.drawRollingNumber = 0;
    },

    async showOneBall(num) {
      if (this.hitNumbers.length >= REVEAL_TOTAL) {
        this.drawIndex = REVEAL_TOTAL;
        return;
      }

      this._isAnimating = true;

      const revealIntervalMs = this.kenoConfig?.revealIntervalMs || 2500;
      const ROLL_MS = Math.floor(revealIntervalMs * 0.72);
      const HOLD_MS = revealIntervalMs - ROLL_MS;

      const timeoutPromise = new Promise((_, reject) => {
        setTimeout(() => reject(new Error("动画超时")), 8000);
      });

      try {
        this.stopRolling();
        this.drawIndex = Math.min(REVEAL_TOTAL, this.hitNumbers.length + 1);

        this.startRolling();
        await Promise.race([this.sleep(ROLL_MS), timeoutPromise]);

        this.stopRolling();
        this.drawRollingNumber = num;

        this.hitNumbers.push(num);
        this.drawSum += Number(num || 0);
        this.drawIndex = this.hitNumbers.length;

        await Promise.race([this.sleep(HOLD_MS), timeoutPromise]);
      } catch (e) {
        console.error("showOneBall error:", e);
        this.stopRolling();
        this.drawRollingNumber = num;
        this.hitNumbers.push(num);
        this.drawSum += Number(num || 0);
        this.drawIndex = this.hitNumbers.length;
        await this.sleep(600);
      } finally {
        this.drawRollingNumber = 0;
        this._isAnimating = false;
      }
    },

    calcWinCodesBySum(sum) {
      const cfg = this.kenoConfig || {};
      const under = cfg.under || {};
      const over = cfg.over || {};
      const ranges = Array.isArray(cfg.ranges) ? cfg.ranges : [];
      const s = Number(sum || 0);

      let ouCode = "";
      const uMin = Number(under.min), uMax = Number(under.max);
      const oMin = Number(over.min), oMax = Number(over.max);

      if (Number.isFinite(uMin) && Number.isFinite(uMax) && s >= uMin && s <= uMax) {
        ouCode = "UNDER";
      } else if (Number.isFinite(oMin) && Number.isFinite(oMax) && s >= oMin && s <= oMax) {
        ouCode = "OVER";
      } else {
        ouCode = s <= 810 ? "UNDER" : "OVER";
      }

      let rangeCode = "";
      for (const r of ranges) {
        const min = Number(r.min), max = Number(r.max);
        if (Number.isFinite(min) && Number.isFinite(max) && s >= min && s <= max) {
          rangeCode = String(r.code || "");
          break;
        }
      }
      rangeCode = String(rangeCode);
      if (rangeCode && !rangeCode.startsWith("R")) rangeCode = "R" + rangeCode;

      return { ouCode, rangeCode };
    },

    // ================== betting ==================
    async onPick({ code }) {
      if (!this.uiBettingAllowed) {
        alert("Betting closed");
        return;
      }

      const cfg = this.kenoConfig || {};
      const under = cfg.under || {};
      const over = cfg.over || {};
      const ranges = Array.isArray(cfg.ranges) ? cfg.ranges : [];

      let playLabel = code;
      let rangeText = "-";
      let oddsText = "-";

      if (code === "UNDER") {
        playLabel = this.$t("lotto.under");
        rangeText = `${under.min || "-"}-${under.max || "-"}`;
        oddsText = String(under.odds != null ? under.odds : "-");
      } else if (code === "OVER") {
        playLabel = this.$t("lotto.over");
        rangeText = `${over.min || "-"}-${over.max || "-"}`;
        oddsText = String(over.odds != null ? over.odds : "-");
      } else {
        const r = ranges.find(x => String(x.code) === String(code));
        playLabel = String(code).replace("R", "");
        if (r) {
          rangeText = `${r.min != null ? r.min : "-"}-${r.max != null ? r.max : "-"}`;
          oddsText = String(r.odds != null ? r.odds : "-");
        }
      }

      this.betModalPayload = { code, playLabel, rangeText, oddsText };
      this.betModalVisible = true;
    },

    onBetModalCancel() {
      this.betModalVisible = false;
    },

    async onBetModalConfirm({ amount }) {
      const code = this.betModalPayload.code;
      const minBet = Number(this.kenoConfig.minBet || 0);
      const maxBet = Number(this.kenoConfig.maxBet || 0);

      if (!Number.isFinite(amount) || amount <= 0) {
        alert("Invalid amount");
        return;
      }
      if (minBet && amount < minBet) {
        alert(`Min bet: ${minBet}`);
        return;
      }
      if (maxBet && amount > maxBet) {
        alert(`Max bet: ${maxBet}`);
        return;
      }
      if (!this.uiBettingAllowed) {
        alert("Betting closed");
        this.betModalVisible = false;
        return;
      }

      try {
        await placeBetApi(code, amount);
        this.betModalVisible = false;

        await Promise.allSettled([
          this.loadTicketsFromHistory(),
          this.loadBalance()
        ]);
      } catch (e) {
        console.error("投注失败:", e);
        alert("Bet failed");
      }
    },

    // ================== util ==================
    async forceRefreshLeft() {
      try {
        await Promise.allSettled([
          this.loadBalance(),
          this.refreshLastSettledBundle(),
          this.loadTicketsFromHistory()
        ]);
      } catch (e) {
        console.error("forceRefreshLeft error", e);
      }
    },

    sleep(ms) {
      return new Promise(resolve => setTimeout(resolve, ms));
    }
  }
};
</script>

<style scoped>
.page {
  min-height: 100vh;
  background: #f3f3f3;
}

.layout {
  display: flex;
  padding-top: 0;
  height: calc(100vh - 121px);
  min-width: 1736px;
}

.main {
  flex: 1;
  overflow: hidden;
  background: #fff;
}
</style>